/**
 * C02 Battle Pass UI — home tier-track banner + claim button + season-reset modal.
 *
 * Three surfaces:
 *   1. `renderSeasonPassBanner` — home page widget: season title + day counter +
 *      progress bar to next tier + horizontal tier strip + 「✨ N 個獎勵可領」 CTA.
 *   2. `renderSeasonResetModal`  — fires once when `tickSeason` returns reset
 *      info; shows "上季成果" summary with new-record celebration.
 *   3. `processSeasonOnHomeRender` (re-exported) — single call site for home.ts.
 *
 * Pure presentation — all state mutation goes through `season-pass.ts`.
 * Inline styles (matches dayone-banner / streak-ui / badges-ui pattern).
 */

import type { PlayerProgress } from '../../shared/types';
import {
  processSeasonOnHomeEnter,
  claimAllPending,
  claimTier,
  type SeasonSnapshot,
  type SeasonResetInfo,
  type SeasonTier,
} from './season-pass';

const STYLE_ID = 'season-pass-ui-style';

function ensureStyleInjected(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes season-tier-pulse {
      0%   { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,193,7,0.7); }
      50%  { transform: scale(1.08); box-shadow: 0 0 12px 4px rgba(255,193,7,0.4); }
      100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,193,7,0); }
    }
    .season-pass-tier-claimable {
      animation: season-tier-pulse 1.4s ease-in-out infinite;
    }
    .season-pass-modal-backdrop {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.55);
      display: flex; align-items: center; justify-content: center;
      z-index: 200;
    }
    .season-pass-modal {
      background: linear-gradient(160deg,#1A237E 0%,#311B92 100%);
      color: #fff;
      padding: 28px 32px;
      border-radius: 18px;
      max-width: 420px; width: 90%;
      box-shadow: 0 12px 36px rgba(0,0,0,0.5);
      text-align: center;
    }
    @media (prefers-reduced-motion: reduce) {
      .season-pass-tier-claimable { animation: none !important; }
    }
  `;
  document.head.appendChild(style);
}

export interface SeasonPassBannerOptions {
  /** Snapshot already produced by processSeasonOnHomeEnter (drives render). */
  snapshot: SeasonSnapshot;
  /** Live progress (mutated on claim — banner re-renders itself). */
  progress: PlayerProgress;
  /** Called after any claim so home can refresh peer banners (freeXP etc.). */
  onClaimed?: (claimed: SeasonTier[]) => void;
}

/**
 * Mount the tier-track banner. Returns an unmount function.
 * Hides itself when the season is unseeded (defensive — home.ts should always
 * call processSeasonOnHomeEnter first, which seeds).
 */
export function renderSeasonPassBanner(
  container: HTMLElement,
  options: SeasonPassBannerOptions,
): () => void {
  ensureStyleInjected();
  container.innerHTML = '';
  const { snapshot, progress, onClaimed } = options;

  if (!snapshot.seeded) {
    // Unseeded — render nothing (the very-first home render seeds then this
    // gets called again on the immediate re-render after claim/tick).
    return () => { /* noop */ };
  }

  const root = document.createElement('div');
  root.className = 'season-pass-banner';
  root.setAttribute('data-testid', 'season-pass-banner');
  root.setAttribute('data-season', String(snapshot.seasonNumber));
  root.setAttribute('data-pending', String(snapshot.pendingCount));
  root.style.cssText = [
    'padding:14px 18px',
    'margin:8px 0',
    'border-radius:14px',
    'background:linear-gradient(135deg,#311B92 0%,#5E35B1 60%,#7E57C2 100%)',
    'color:#fff',
    'box-shadow:0 3px 10px rgba(0,0,0,0.25)',
    'font-family:inherit',
  ].join(';');

  // ── Header line: season # + name + day counter ─────────────────────────
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;justify-content:space-between;align-items:baseline;gap:8px;margin-bottom:6px;';
  const title = document.createElement('div');
  title.style.cssText = 'font-size:16px;font-weight:700;line-height:1.2;';
  title.textContent = `🏆 S${snapshot.seasonNumber} · ${snapshot.seasonName}`;
  const dayCount = document.createElement('div');
  dayCount.style.cssText = 'font-size:12px;opacity:0.85;';
  dayCount.textContent = `第 ${snapshot.dayInSeason}/${snapshot.lengthDays} 天 · 還剩 ${snapshot.daysRemaining} 天`;
  header.append(title, dayCount);

  // ── Subtitle ──────────────────────────────────────────────────────────
  const subtitle = document.createElement('div');
  subtitle.style.cssText = 'font-size:12px;opacity:0.75;margin-bottom:10px;';
  subtitle.textContent = snapshot.seasonSubtitle;

  // ── Progress bar + 下一階說明 ──────────────────────────────────────────
  const progressWrap = document.createElement('div');
  progressWrap.style.cssText = 'margin-bottom:10px;';
  const progressBar = document.createElement('div');
  progressBar.setAttribute('data-testid', 'season-pass-progress-bar');
  progressBar.style.cssText = 'height:10px;background:rgba(255,255,255,0.18);border-radius:5px;overflow:hidden;position:relative;';
  const fillPct = computeProgressPct(snapshot);
  const fill = document.createElement('div');
  fill.setAttribute('data-testid', 'season-pass-progress-fill');
  fill.style.cssText = `height:100%;width:${fillPct}%;background:linear-gradient(90deg,#FFD54F 0%,#FFA000 100%);transition:width 0.4s ease;`;
  progressBar.appendChild(fill);
  const progressLabel = document.createElement('div');
  progressLabel.style.cssText = 'font-size:12px;margin-top:4px;opacity:0.9;';
  progressLabel.textContent = progressCopy(snapshot);
  progressWrap.append(progressBar, progressLabel);

  // ── Tier strip ─────────────────────────────────────────────────────────
  const strip = document.createElement('div');
  strip.setAttribute('data-testid', 'season-pass-tier-strip');
  strip.style.cssText = 'display:flex;gap:6px;overflow-x:auto;padding:4px 0 8px;';
  for (const row of snapshot.tiers) {
    strip.appendChild(renderTierCell(row, progress, onClaimed));
  }

  // ── Footer: claim-all CTA + longest record ─────────────────────────────
  const footer = document.createElement('div');
  footer.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:6px;';
  const longest = document.createElement('div');
  longest.style.cssText = 'font-size:11px;opacity:0.7;';
  longest.textContent = `歷史最佳:${snapshot.longestSeasonXp} XP`;

  const claimAllBtn = document.createElement('button');
  claimAllBtn.setAttribute('data-testid', 'season-pass-claim-all');
  claimAllBtn.style.cssText = [
    'background:#FFC107',
    'color:#3E2723',
    'border:none',
    'padding:8px 16px',
    'border-radius:8px',
    'font-size:14px',
    'font-weight:700',
    'cursor:pointer',
    snapshot.pendingCount === 0 ? 'opacity:0.4;cursor:not-allowed;' : '',
  ].join(';');
  claimAllBtn.textContent = snapshot.pendingCount > 0
    ? `✨ 一鍵領取 ${snapshot.pendingCount} 個獎勵`
    : '尚無可領獎勵';
  claimAllBtn.disabled = snapshot.pendingCount === 0;
  claimAllBtn.addEventListener('click', () => {
    if (claimAllBtn.disabled) return;
    const claimed = claimAllPending(progress);
    if (claimed.length > 0 && onClaimed) onClaimed(claimed);
    // Re-render in place — call site re-snapshots from the (mutated) progress.
    const next = processSeasonOnHomeEnter(progress).snapshot;
    renderSeasonPassBanner(container, { snapshot: next, progress, onClaimed });
  });
  footer.append(longest, claimAllBtn);

  root.append(header, subtitle, progressWrap, strip, footer);
  container.appendChild(root);

  return () => { root.remove(); };
}

/** Render a single tier "pill" within the tier strip. */
function renderTierCell(
  row: SeasonSnapshot['tiers'][number],
  progress: PlayerProgress,
  onClaimed?: (claimed: SeasonTier[]) => void,
): HTMLElement {
  const cell = document.createElement('button');
  cell.setAttribute('data-testid', `season-pass-tier-${row.def.tier}`);
  cell.setAttribute('data-tier-claimed', row.claimed ? 'true' : 'false');
  cell.setAttribute('data-tier-unlocked', row.unlocked ? 'true' : 'false');
  cell.setAttribute('data-tier-claimable', row.claimable ? 'true' : 'false');

  const bg = row.claimed
    ? 'background:rgba(76,175,80,0.4);'
    : row.unlocked
      ? 'background:rgba(255,193,7,0.85);color:#3E2723;'
      : 'background:rgba(255,255,255,0.12);';
  cell.style.cssText = [
    'min-width:88px',
    'flex:0 0 auto',
    'padding:8px 6px',
    'border-radius:10px',
    'border:none',
    'color:#fff',
    'font-family:inherit',
    'text-align:center',
    'cursor:' + (row.claimable ? 'pointer' : 'default'),
    bg,
  ].join(';');

  if (row.claimable) cell.classList.add('season-pass-tier-claimable');

  const tierNum = document.createElement('div');
  tierNum.style.cssText = 'font-size:11px;opacity:0.85;';
  tierNum.textContent = `T${row.def.tier}`;
  const name = document.createElement('div');
  name.style.cssText = 'font-size:13px;font-weight:700;line-height:1.15;margin:2px 0;';
  name.textContent = row.def.name;
  const reward = document.createElement('div');
  reward.style.cssText = 'font-size:10px;opacity:0.85;line-height:1.2;';
  reward.textContent = row.def.rewardLabel;
  const status = document.createElement('div');
  status.style.cssText = 'font-size:10px;margin-top:3px;';
  status.textContent = row.claimed
    ? '✅ 已領取'
    : row.unlocked
      ? '✨ 可領取'
      : `🔒 ${row.def.xpRequired} XP`;

  cell.append(tierNum, name, reward, status);

  if (row.claimable) {
    cell.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const def = claimTier(progress, row.def.tier);
      if (def && onClaimed) onClaimed([def]);
      // Bubble a re-render request — easiest: trigger a custom event the
      // host page listens to. For simplicity we just no-op here; the
      // banner refresh happens when claim-all is hit OR home re-renders.
      cell.setAttribute('data-tier-claimed', 'true');
      cell.classList.remove('season-pass-tier-claimable');
      status.textContent = '✅ 已領取';
    });
  }

  return cell;
}

function computeProgressPct(s: SeasonSnapshot): number {
  // % toward NEXT tier (within the current tier slice). If no next, 100%.
  if (s.next === null) return 100;
  const prevReq = s.highestUnlocked > 0
    ? (s.tiers.find((t) => t.def.tier === s.highestUnlocked)?.def.xpRequired ?? 0)
    : 0;
  const denom = s.next.xpRequired - prevReq;
  if (denom <= 0) return 100;
  const num = Math.max(0, s.xpThisSeason - prevReq);
  return Math.min(100, Math.round((num / denom) * 100));
}

function progressCopy(s: SeasonSnapshot): string {
  if (s.next === null) return `🐉 賽季滿級 · ${s.xpThisSeason} XP`;
  return `${s.xpThisSeason} XP · 再 ${s.xpToNext} XP 解鎖 T${s.next.tier} ${s.next.name}`;
}

// ── Reset modal ─────────────────────────────────────────────────────────────

/**
 * One-shot modal shown when `tickSeason` returns reset info (i.e. the new
 * season just rolled over). Wraps a 「上季成果」 summary with a 開始新賽季 button.
 *
 * Caller is responsible for only mounting this once per reset event — easiest
 * pattern: home.ts checks `result.resetInfo !== null` and calls this.
 *
 * Returns an unmount function (the modal also self-removes on the CTA tap).
 */
export function renderSeasonResetModal(
  hostContainer: HTMLElement,
  info: SeasonResetInfo,
  newSeasonNumber: number,
  onDismissed?: () => void,
): () => void {
  ensureStyleInjected();

  const backdrop = document.createElement('div');
  backdrop.className = 'season-pass-modal-backdrop';
  backdrop.setAttribute('data-testid', 'season-pass-reset-modal');
  backdrop.setAttribute('data-ended-season', String(info.endedSeason));

  const modal = document.createElement('div');
  modal.className = 'season-pass-modal';

  const headline = document.createElement('div');
  headline.style.cssText = 'font-size:24px;font-weight:800;margin-bottom:8px;';
  headline.textContent = `🎉 S${info.endedSeason} 賽季結束!`;

  const newRecord = info.newRecord
    ? `<div style="font-size:14px;color:#FFD740;font-weight:700;margin:4px 0;">🏆 打破歷史紀錄!</div>`
    : '';

  const body = document.createElement('div');
  body.style.cssText = 'font-size:14px;line-height:1.5;margin:12px 0;';
  body.innerHTML = `
    ${newRecord}
    <div>累積 seasonXP:<strong>${info.xpEarned}</strong></div>
    <div>達到階級:T${info.highestTierReached} / ${info.totalTiers}</div>
    <div>領取獎勵:${info.tiersClaimed.length} 件</div>
    ${info.tiersClaimed.length < info.highestTierReached
      ? `<div style="font-size:12px;color:#FFAB91;margin-top:6px;">⚠️ 有 ${info.highestTierReached - info.tiersClaimed.length} 件未領取的獎勵已過期</div>`
      : ''}
  `;

  const next = document.createElement('div');
  next.style.cssText = 'font-size:13px;opacity:0.85;margin:12px 0;';
  next.textContent = `下個賽季:S${newSeasonNumber}`;

  const btn = document.createElement('button');
  btn.setAttribute('data-testid', 'season-pass-reset-dismiss');
  btn.style.cssText = [
    'background:#FFC107',
    'color:#3E2723',
    'border:none',
    'padding:10px 24px',
    'border-radius:10px',
    'font-size:16px',
    'font-weight:700',
    'cursor:pointer',
    'margin-top:8px',
  ].join(';');
  btn.textContent = '開始新賽季!';
  const unmount = () => {
    backdrop.remove();
  };
  btn.addEventListener('click', () => {
    unmount();
    if (onDismissed) onDismissed();
  });

  modal.append(headline, body, next, btn);
  backdrop.appendChild(modal);
  hostContainer.appendChild(backdrop);

  return unmount;
}
